import type { ArtefactAdded, ArtefactKnowledgePreview, FolderId } from "@esi/shared";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { TFunction } from "i18next";
import { FolderClosed } from "lucide-react";
import { useEffect, useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { AppDialog } from "@/components/AppDialog";
import { Notice } from "@/components/Notice";
import { Button } from "@/components/ui/button";
import { DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { ApiError } from "../../api-errors.ts";
import { canChange } from "../../projects/capabilities.ts";
import { FolderField } from "../../projects/FolderPicker.tsx";
import { projectKeys, useProjects } from "../../projects/queries.ts";
import { shortId } from "../../projects/readers.ts";
import { Link, projectHomePath } from "../../router.tsx";
import { useSession } from "../../session.tsx";
import { AudienceLine } from "../../trust/AudienceLine.tsx";
import { peopleAudience } from "../../trust/audience.ts";
import { GeneratedMark } from "../../trust/GeneratedMark.tsx";
import { TargetPicker } from "../SaveDialog.tsx";
import {
  type ConversationScope,
  knowledgeKeys,
  postKnowledge,
  useKnowledgePreview,
} from "./queries.ts";

/**
 * "Add to knowledge" for one artefact version (openspec change `artefacts-to-knowledge`, design
 * §9), modelled on saving an answer as a file: before confirming, it says who will be able to
 * read the file (its sources decide, live), its classification, that it is generated by the
 * agent, and lets the user name it and pick its Files folder. From a standalone conversation the
 * project comes first. When an earlier version is in knowledge, it becomes "Update in knowledge".
 */
export function AddToKnowledgeDialog(props: {
  open: boolean;
  onClose: () => void;
  scope: ConversationScope;
  artefactId: string;
  version: number;
  /** The version's title, the file name's default. */
  title: string;
  returnFocus?: HTMLElement | null | (() => HTMLElement | null);
}) {
  const { t } = useTranslation();
  // Kept here: the form is remounted per picked project, the typed name stays.
  const typed = useState<string | null>(null);
  const [mode, setMode] = useState<ArtefactKnowledgePreview["state"] | null>(null);
  const title =
    mode === "update" ? t("artefactKnowledge.updateTitle") : t("artefactKnowledge.addTitle");
  return (
    <AppDialog
      open={props.open}
      onClose={props.onClose}
      title={title}
      returnFocus={props.returnFocus}
    >
      {props.scope.projectId ? (
        <KnowledgeForm
          {...props}
          projectId={props.scope.projectId}
          target={null}
          typed={typed}
          onMode={setMode}
        />
      ) : (
        <TargetPicker capability="file.upload">
          {(target) => (
            <KnowledgeForm
              key={target}
              {...props}
              projectId={target}
              target={target}
              typed={typed}
              onMode={setMode}
            />
          )}
        </TargetPicker>
      )}
    </AppDialog>
  );
}

function KnowledgeForm(props: {
  onClose: () => void;
  scope: ConversationScope;
  artefactId: string;
  version: number;
  title: string;
  /** Where the file goes: the conversation's project, or the picked target. */
  projectId: string;
  target: string | null;
  typed: [string | null, (value: string | null) => void];
  onMode: (mode: ArtefactKnowledgePreview["state"] | null) => void;
}) {
  const { onClose, scope, artefactId, version, projectId, target, onMode } = props;
  const { t } = useTranslation();
  const id = useId();
  const { csrfToken, userId } = useSession();
  const queryClient = useQueryClient();
  // The Files root unless the user picks a folder; the preview follows the pick (its path).
  const [folderId, setFolderId] = useState<FolderId | null>(null);
  const preview = useKnowledgePreview(scope, artefactId, version, {
    target,
    folderId,
    enabled: true,
  });
  const state = preview.data?.state ?? null;
  const [name, setName] = props.typed;
  const fileName = name ?? preview.data?.title ?? "";
  const projects = useProjects();
  const canOrganise = projects.data?.some(
    (p) => p.id === projectId && canChange(p, "folder.manage"),
  );
  const [done, setDone] = useState<ArtefactAdded | null>(null);
  const write = useMutation({
    mutationFn: () =>
      postKnowledge(scope, artefactId, version, {
        update: state === "update",
        ...(fileName.trim() && state === "add" ? { title: fileName.trim() } : {}),
        target,
        folderId: state === "add" ? folderId : null,
        csrfToken,
      }),
    onSuccess: (added) => {
      setDone(added);
      void queryClient.invalidateQueries({ queryKey: projectKeys.documents(added.projectId) });
      void queryClient.invalidateQueries({ queryKey: knowledgeKeys.all(artefactId) });
    },
  });
  // The dialog's title follows what confirming will do.
  useEffect(() => {
    onMode(done ? null : state);
  }, [state, done, onMode]);

  if (done) {
    return (
      <div className="flex flex-col gap-4">
        <Notice tone="success" live>
          {t("artefactKnowledge.done", { title: done.title, version: done.documentVersion })}
        </Notice>
        <DialogFooter>
          <Button variant="outline" asChild>
            <Link href={`${projectHomePath(done.projectId, "files")}?document=${done.documentId}`}>
              {t("artefactKnowledge.showInFiles")}
            </Link>
          </Button>
          <Button onClick={onClose}>{t("save.done")}</Button>
        </DialogFooter>
      </div>
    );
  }

  const p = preview.data;
  const level = p?.level?.label ?? p?.classification ?? "";
  return (
    <form
      className="flex flex-col gap-5"
      onSubmit={(e) => {
        e.preventDefault();
        if (p && offered(p) && !write.isPending) {
          write.mutate();
        }
      }}
    >
      {preview.isPending ? (
        <Skeleton className="h-32 w-full" />
      ) : preview.isError ? (
        <Notice tone="danger">{knowledgeRefusal(preview.error, t)}</Notice>
      ) : p ? (
        <>
          {p.state === "update" && !p.existing?.canUpdate ? (
            <Notice tone="neutral">{t("artefactKnowledge.cannotUpdate")}</Notice>
          ) : null}
          {p.state === "added" ? (
            <Notice tone="neutral">
              {t("artefactKnowledge.alreadyAdded", {
                version: p.existing?.artefactVersion ?? version,
              })}
            </Notice>
          ) : null}
          <section aria-label={t("audience.label")} className="flex flex-col gap-2">
            <AudienceLine
              restricted={p.audience === "named"}
              // The icon stays with the first line when the words wrap (phones, French).
              className="flex-nowrap items-start text-sm font-medium text-fg [&>svg]:mt-0.5"
              text={
                p.audience === "named"
                  ? peopleAudience(
                      p.readerCount,
                      p.readers.length === 1 && p.readers[0]?.userId === userId,
                    )
                  : t("artefactKnowledge.projectAudience", { count: p.readerCount })
              }
            />
            <p className="text-sm text-fg-secondary">{t("artefactKnowledge.readersExplain")}</p>
            {p.audience === "named" ? (
              <ul className="max-h-40 divide-y divide-line overflow-y-auto rounded-lg border border-line bg-surface-raised text-sm">
                {p.readers.map((r) => (
                  <li key={r.userId} className="px-3 py-2 text-fg">
                    {r.displayName ?? shortId(r.userId)}
                    {r.userId === userId ? (
                      <span className="text-fg-muted"> ({t("members.you")})</span>
                    ) : null}
                  </li>
                ))}
              </ul>
            ) : null}
          </section>
          <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 text-sm">
            <dt className="text-fg-muted">{t("save.classification")}</dt>
            <dd className="text-fg">{level}</dd>
            <dt className="text-fg-muted">{t("save.origin")}</dt>
            <dd className="flex flex-wrap items-center gap-x-1.5 text-fg">
              <GeneratedMark className="text-sm" />
              {p.origin === "external" ? (
                <span className="text-fg-secondary">· {t("artefactKnowledge.withWeb")}</span>
              ) : null}
            </dd>
          </dl>
          <p className="text-sm text-fg-secondary">{t("artefactKnowledge.generatedExplain")}</p>
          {p.state === "update" ? (
            <p className="flex items-start gap-1.5 text-sm text-fg-secondary">
              <FolderClosed
                aria-hidden="true"
                className="mt-0.5 size-4 shrink-0 text-fg-muted"
                strokeWidth={1.75}
              />
              {t("artefactKnowledge.updateExplain", {
                current: p.existing?.artefactVersion ?? 1,
                version,
                folder:
                  p.folderPath.length > 0
                    ? p.folderPath.join(" › ")
                    : t("knowledgePanel.section.files"),
              })}
            </p>
          ) : p.state === "add" ? (
            <>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor={`${id}-name`}>{t("save.fileTitle")}</Label>
                <Input
                  id={`${id}-name`}
                  value={fileName}
                  maxLength={255}
                  onChange={(e) => setName(e.currentTarget.value)}
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <FolderField
                  projectId={projectId}
                  section="files"
                  value={folderId}
                  onChange={setFolderId}
                  canCreate={canOrganise}
                />
                <p className="text-xs text-fg-muted">{t("artefactKnowledge.folderExplain")}</p>
              </div>
            </>
          ) : null}
        </>
      ) : null}
      {write.isError ? <Notice tone="danger">{knowledgeRefusal(write.error, t)}</Notice> : null}
      <DialogFooter sticky>
        <Button type="button" variant="outline" onClick={onClose}>
          {p && !offered(p) ? t("save.done") : t("save.cancel")}
        </Button>
        {p && !offered(p) ? null : (
          <Button
            type="submit"
            disabled={!p || write.isPending || (state === "add" && !fileName.trim())}
          >
            {write.isPending
              ? t("artefactKnowledge.adding")
              : state === "update"
                ? t("artefactKnowledge.updateSubmit")
                : t("artefactKnowledge.addSubmit")}
          </Button>
        )}
      </DialogFooter>
    </form>
  );
}

/** Whether confirming would do something the viewer may do: add, or update when allowed. */
const offered = (p: ArtefactKnowledgePreview) =>
  p.state === "add" || (p.state === "update" && p.existing?.canUpdate === true);

/** A refusal in plain words where the user can act on it; else the server's message. */
export function knowledgeRefusal(error: Error, t: TFunction): string {
  if (!(error instanceof ApiError)) {
    return error.message;
  }
  switch (error.code) {
    case "residency_narrower":
      return t("save.residencyNarrower");
    case "already_added":
      return t("artefactKnowledge.refusal.alreadyAdded");
    case "not_newer":
      return t("artefactKnowledge.refusal.notNewer");
    case "storage_limit":
      return t("artefactKnowledge.refusal.storageLimit");
    case "project_frozen":
      return t("artefactKnowledge.refusal.frozen");
    default:
      return error.status === 403 ? t("artefactKnowledge.refusal.forbidden") : error.message;
  }
}
